import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFairyChat } from "../hooks/useFairyChat";
import StripePayment from "./Stripe/StripePayment-fortesting";
import {
  IoArrowBack,
  IoClose,
  IoChevronDown,
  IoSparkles,
  IoChatbubbleEllipsesOutline,
  IoCheckmarkCircle,
  IoWarning,
  IoDiamond,
} from "react-icons/io5";
import { IoIosSend } from "react-icons/io";
import { GrAttachment } from "react-icons/gr";
import { GiCrystalBall } from "react-icons/gi";
import health from "./Assets/chat/test.png";
import wealth from "./Assets/chat/test2.png";
import wisdom from "./Assets/chat/test3.png";
import career from "./Assets/chat/test2.png";
import relationship from "./Assets/chat/test.png";

/* ─────────────────────────────────────────────────────────── */
/*  Typing indicator — three bouncing dots                     */
/* ─────────────────────────────────────────────────────────── */


const TYPING_STYLE = `
  @keyframes fairyTypingBounce {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
    30% { transform: translateY(-5px); opacity: 1; }
  }
  .fairy-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%;
    background: #1a1a1a; animation: fairyTypingBounce 1.2s infinite; }
  .fairy-dot:nth-child(2) { animation-delay: 0.2s; }
  .fairy-dot:nth-child(3) { animation-delay: 0.4s; }
`;

const SUGGEST_PILL_STYLE = `
  @keyframes fairySuggestIn {
    from { transform: translateY(6px); opacity: 0; }
    to   { transform: translateY(0);   opacity: 1; }
  }
  @keyframes fairySuggestPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(223,189,105,0.35); }
    50%      { box-shadow: 0 0 0 6px rgba(223,189,105,0); }
  }
  .fairy-suggest-pill {
    animation: fairySuggestIn 0.25s ease-out,
               fairySuggestPulse 2s ease-out 0.25s 3;
  }
`;

const TypingIndicator = () => (
  <>
    <style>{TYPING_STYLE}</style>
    <div
      style={{
        maxWidth: "75%",
        padding: "10px 16px",
        borderRadius: "12px",
        backgroundColor: "#DFBD69",
        alignSelf: "flex-start",
        display: "flex",
        alignItems: "center",
        gap: "5px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
      }}
    >
      <span className="fairy-dot" />
      <span className="fairy-dot" />
      <span className="fairy-dot" />
    </div>
  </>
);

/* ─────────────────────────────────────────────────────────── */
/*  Viewport hook — the sidebar layout can't survive < 900px    */
/* ─────────────────────────────────────────────────────────── */
const useIsMobile = (breakpoint = 900) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
};

/* ─────────────────────────────────────────────────────────── */
/*  Static data                                                */
/* ─────────────────────────────────────────────────────────── */
// At one question left the next one is the last, so the packs surface then rather than
// after the user has already been cut off mid-thought.
const LOW_CHAT_CREDIT = 1;

const CATEGORIES = [
  {
    key: "wealth",
    prompt: "Wealth",
    defaultPrompt: "monthly wealth",
    icon: wealth,
    color: "#deaf77",
  },
  {
    key: "career",
    prompt: "Career",
    defaultPrompt: "monthly career",
    icon: career,
    color: "#deaf77",
  },
  {
    key: "relationship",
    prompt: "Relationship",
    defaultPrompt: "monthly relationship",
    icon: relationship,
    color: "#deaf77",
  },
  {
    key: "health",
    prompt: "Health",
    defaultPrompt: "monthly health",
    icon: health,
    color: "#deaf77",
  },
  {
    key: "wisdom",
    prompt: "Wisdom",
    defaultPrompt: "monthly wisdom",
    icon: wisdom,
    color: "#deaf77",
  },
];

/* ─────────────────────────────────────────────────────────── */
/*  Chat page component                                        */
/* ─────────────────────────────────────────────────────────── */
const Chat = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const { messages, loading, suggestions, shopSuggestions, consultationSuggestions, chatProducts, purchaseChatProduct, chatCredit, outOfCredit, sendMessage, selectCategory, resetChat } = useFairyChat();

  const [input, setInput] = useState("");
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [panelSeen, setPanelSeen] = useState(false);
  const [scrollRequest, setScrollRequest] = useState(0);
  const [showTopUp, setShowTopUp] = useState(false);
  const [pendingPack, setPendingPack] = useState(null); // pack awaiting payment choice
  const [packQty, setPackQty] = useState(1);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null); // { ok, message }
  const [stripeOpen, setStripeOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading, suggestions, shopSuggestions, consultationSuggestions]);

  /* New recommendations arrive collapsed — they announce themselves with the
     pill instead of taking over the view while the answer is still being read.
     Keyed on the question count, not on the suggestion arrays: shop and
     consultation resolve in separate async calls, so watching the arrays would
     snap the panel shut under a user who had just opened it. */
  const questionCount = messages.filter((m) => m.sender === "user").length;
  useEffect(() => setSuggestionsOpen(false), [questionCount]);

  const hasShop = shopSuggestions.length > 0;
  const hasConsultation = consultationSuggestions.length > 0;
  const hasSuggestions = hasShop || hasConsultation;
  const panelOpen = hasSuggestions && suggestionsOpen;
  const suggestionCount = shopSuggestions.length + consultationSuggestions.length;

  /* On mobile the panel stacks below the thread, so recommendations land
     off-screen while the user is still reading the reply. Track whether it's
     actually visible and nudge with the pill only while it isn't. */
  useEffect(() => {
    const el = panelRef.current;
    if (!isMobile || !panelOpen || !el) {
      setPanelSeen(false);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setPanelSeen(entry.isIntersecting),
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isMobile, panelOpen]);

  // The panel may be mounting in this same render, so scroll from an effect
  // once the ref is attached rather than inline on the click.
  useEffect(() => {
    if (scrollRequest) {
      panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [scrollRequest]);

  const openSuggestions = () => {
    setSuggestionsOpen(true);
    setScrollRequest((n) => n + 1);
  };

  // The pill is how suggestions are offered at all: it opens the panel, and on
  // mobile also resurfaces one that's open but scrolled out of view.
  const showSuggestionPill =
    hasSuggestions && (!panelOpen || (isMobile && !panelSeen));

  /* Same wording in the panel header and the pill. The count goes in as `n`,
     not `count`, so i18next resolves the key we name instead of appending its
     own plural suffix — languages here include ones with no plural forms. */
  const suggestionLabel = t(
    suggestionCount === 1
      ? "chat.suggestionsPanel.titleOne"
      : "chat.suggestionsPanel.titleOther",
    { n: suggestionCount }
  );

  /* Running low: the top-up panel opens itself once per dip below the threshold —
     reopening it after a manual close would just be nagging. The packs are reachable
     from the button beside the input the rest of the time. */
  const lowOnCredit = chatCredit != null && chatCredit <= LOW_CHAT_CREDIT;
  const autoOpenedRef = useRef(false);

  useEffect(() => {
    if (!lowOnCredit) {
      autoOpenedRef.current = false;
      return;
    }
    if (!autoOpenedRef.current && chatProducts.length > 0) {
      autoOpenedRef.current = true;
      setShowTopUp(true);
    }
  }, [lowOnCredit, chatProducts.length]);


  const handleSend = (text, questionId = null, queryOverride = null, options = {}) => {
    if (!text.trim()) return;
    // Out of questions: the top-up panel is the only way forward from here.
    if (outOfCredit) {
      setShowTopUp(chatProducts.length > 0);
      return;
    }
    setInput("");
    sendMessage(queryOverride || text, questionId, text, options);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(input);
    }
  };

  /* Picking a pack opens the confirm step — quantity and pay mode are chosen there. */
  const handleSelectChatProduct = (pack) => {
    setPackQty(1);
    setPendingPack(pack);
  };

  const packTotalCash = pendingPack ? pendingPack.cash * packQty : 0;
  const packTotalGems = pendingPack ? pendingPack.gem * packQty : 0;

  /* PayMode 0 — gems come straight off the balance, so the record is the payment.
     The hook checks the balance first and reports a shortfall rather than a bare failure. */
  const handleGemPurchase = async () => {
    if (!pendingPack || purchasing) return;
    setPurchasing(true);

    const result = await purchaseChatProduct({
      id: pendingPack.id,
      qty: packQty,
      payMode: 0,
    });

    setPurchasing(false);
    setPendingPack(null);
    setShowTopUp(false);
    setPurchaseResult({
      ok: result.successful,
      insufficientGems: !!result.insufficientGems,
      message:
        result.message ||
        (result.successful
          ? t("chat.purchase.successMessage")
          : t("chat.purchase.failedMessage")),
    });
  };

  /* PayMode 1 — Stripe takes the money first, then the purchase is recorded. */
  const handleCardPurchase = () => {
    if (!pendingPack) return;
    setStripeOpen(true);
  };

  const handleCardPaymentSuccess = async () => {
    setStripeOpen(false);
    if (!pendingPack) return;
    setPurchasing(true);

    const result = await purchaseChatProduct({
      id: pendingPack.id,
      qty: packQty,
      payMode: 1,
    });

    setPurchasing(false);
    setPendingPack(null);
    setShowTopUp(false);
    setPurchaseResult({
      ok: result.successful,
      message: result.successful
        ? result.message || t("chat.purchase.successMessage")
        : t("chat.purchase.cardRecordFailed"),
    });
  };

  /* ─────────────────────────────────────────────────────── */
  /*  Render                                                 */
  /* ─────────────────────────────────────────────────────── */
  /* Mobile collapses the two-column layout into one scrolling stack —
     at 425px the fixed 260px sidebar left the chat column ~80px wide. */
  const mq = (base, mobileOverrides) =>
    isMobile ? { ...base, ...mobileOverrides } : base;

  return (
    <div style={mq(styles.page, styles.pageMobile)}>

      {/* ── Back button ── */}
      <div style={mq(styles.backRow, styles.backRowMobile)}>
        <button style={styles.backBtn} onClick={() => navigate(-1)}>
          <IoArrowBack size={isMobile ? 24 : 32} style={{ marginRight: 6 }} />
          <span style={{ color: "#f8f5fc" }}>{t("chat.title")}</span>
        </button>
      </div>

      {/* ── Main content area ── */}
      <div style={mq(styles.body, styles.bodyMobile)}>
        <div style={mq(styles.chatColumn, styles.chatColumnMobile)}>
          <div style={styles.thread}>

            {/* ════ WELCOME CONTENT — always visible, scrolls up when chat starts ════ */}
            <div style={styles.welcome}>
              <div style={mq(styles.welcomeBox, styles.welcomeBoxMobile)}>
                <p style={styles.promptHeading}>
                  {t("chat.randomQuestionsPromptLine1")}
                </p>
              </div>

              <div style={styles.categoryRow}>
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.key}
                    style={outOfCredit ? { ...styles.catCard, ...styles.catCardDisabled } : styles.catCard}
                    disabled={outOfCredit}
                    onClick={() => selectCategory(cat.key, t(`chat.categories.${cat.key}.label`))}
                  >
                    <img src={cat.icon} alt={t(`chat.categories.${cat.key}.label`)} style={styles.catIcon} />
                    <span style={{ ...styles.catLabel, color: cat.color }}>
                      {t(`chat.categories.${cat.key}.label`)}
                    </span>
                    <span style={styles.catDesc}>{t(`chat.categories.${cat.key}.desc`)}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* ════ CHAT MESSAGES ════ */}
            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  ...mq(styles.bubble, styles.bubbleMobile),
                  alignSelf: msg.sender === "user" ? "flex-end" : "flex-start",
                  backgroundColor: msg.sender === "user" ? "#DFBD69" : "#2a2a2a",
                  color: msg.sender === "user" ? "#1a1a1a" : "#f5f5f5",
                }}
              >
                {typeof msg.text === "string" && msg.text.includes("<div") ? (
                  <div dangerouslySetInnerHTML={{ __html: msg.text }} />
                ) : (
                  msg.text
                )}
              </div>
            ))}

            {/* Typing indicator */}
            {loading && <TypingIndicator />}

            {/* Follow-up suggestion chips */}
            {suggestions.length > 0 && (
              <div style={styles.chips}>
                {suggestions.map((s) => (
                  <button
                    key={s.id || s.text}
                    style={outOfCredit ? { ...styles.chip, ...styles.chipDisabled } : styles.chip}
                    disabled={outOfCredit}
                    onClick={() =>
                      handleSend(s.text, s.id || null, s.query || null, {
                        // Follow-up chips continue the reading they were generated for.
                        contextChart: s.contextChart || null,
                      })
                    }
                  >
                    {s.text}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* ════ SUGGESTION PANEL — shop & consultation live beside the chat ════ */}
        {panelOpen && (
          <div ref={panelRef} style={mq(styles.sidebar, styles.sidebarMobile)}>
            {/* Single panel header — the one place suggestions are closed from,
                and the label the pill below the thread mirrors */}
            <div style={styles.panelHeader}>
              <p style={styles.panelTitle}>
                <IoSparkles size={13} style={styles.labelIcon} />
                {suggestionLabel}
              </p>
              <button
                style={mq(styles.panelCloseBtn, styles.panelCloseBtnMobile)}
                onClick={() => setSuggestionsOpen(false)}
                aria-label={t("chat.suggestionsPanel.hide")}
                title={t("chat.suggestionsPanel.hide")}
              >
                <IoClose size={18} />
              </button>
            </div>

            {/* Shop suggestions strip */}
            {hasShop && (
              <div style={styles.shopStrip}>
                <p style={styles.shopStripLabel}>
                  {t("chat.suggestionsPanel.shopLabel")}
                </p>
                <div style={mq(styles.shopCards, styles.shopCardsMobile)}>
                  {shopSuggestions.map((p) => (
                    <button
                      key={p.id}
                      style={mq(styles.shopCard, styles.cardMobile)}
                      onClick={() => navigate("/shop", { state: { openProductId: p.id } })}
                    >
                      {p.image ? (
                        <img src={p.image} alt={p.name} style={styles.shopCardImg} />
                      ) : (
                        <div style={styles.shopCardImgPlaceholder} />
                      )}
                      <span style={styles.shopCardName}>{p.name}</span>
                      <span style={styles.shopCardPrice}>
                        {p.charm > 0 ? (
                          <>
                            {p.charm}
                            <IoDiamond size={12} style={styles.gemIcon} />
                          </>
                        ) : p.price > 0 ? (
                          `${p.price}`
                        ) : (
                          ""
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Consultation suggestion strip — AI-matched master based on conversation topic */}
            {hasConsultation && (
              <div style={styles.consultationStrip}>
                <p style={styles.shopStripLabel}>
                  <GiCrystalBall size={14} style={styles.labelIcon} />
                  {t("chat.suggestionsPanel.consultationLabel")}
                </p>
                <div style={mq(styles.shopCards, styles.shopCardsMobile)}>
                  {consultationSuggestions.map((master) => (
                    <button
                      key={master.id}
                      style={mq(styles.consultationCard, styles.cardMobile)}
                      onClick={() =>
                        navigate("/schedule2", { state: { selectedMaster: master } })
                      }
                    >
                      {master.image ? (
                        <img src={master.image} alt={master.name} style={styles.consultationCardImg} />
                      ) : (
                        <div style={styles.consultationCardImgPlaceholder} />
                      )}
                      <span style={styles.consultationCardName}>{master.name}</span>
                      {master.matchReason && (
                        <span style={styles.consultationCardReason}>{master.matchReason}</span>
                      )}
                      <span style={styles.consultationCardCta}>
                        {t("chat.suggestionsPanel.bookConsultation")}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Input bar (always visible at bottom) ── */}
      <div style={mq(styles.inputBar, styles.inputBarMobile)}>

        {/* Re-opens a closed panel, and on mobile surfaces one that's below the fold */}
        {showSuggestionPill && (
          <>
            <style>{SUGGEST_PILL_STYLE}</style>
            <div style={styles.suggestHintRow}>
              <button
                className="fairy-suggest-pill"
                style={styles.suggestHintBtn}
                onClick={openSuggestions}
                aria-label={t("chat.suggestionsPanel.show")}
              >
                <span style={styles.suggestHintDot} />
                {suggestionLabel}
                <IoChevronDown size={14} />
              </button>
            </div>
          </>
        )}

        {/* ── Question top-up packs (api/chat/ListChatProduct) ── */}
        {chatProducts.length > 0 && showTopUp && (
          <div style={mq(styles.topUpPanel, styles.topUpPanelMobile)}>
            <p style={styles.topUpLabel}>
              <IoChatbubbleEllipsesOutline size={13} style={styles.labelIcon} />
              {chatCredit != null
                ? t("chat.topUp.titleWithCount", { n: chatCredit })
                : t("chat.topUp.title")}
            </p>
            <div style={mq(styles.topUpCards, styles.topUpCardsMobile)}>
              {chatProducts.map((pack) => (
                <button
                  key={pack.id}
                  style={mq(styles.topUpCard, styles.cardMobile)}
                  onClick={() => handleSelectChatProduct(pack)}
                >
                  <span style={styles.topUpCardCount}>
                    {pack.questions != null ? pack.questions : pack.label}
                  </span>
                  <span style={styles.topUpCardName}>{pack.label}</span>
                  <span style={styles.topUpCardPrice}>
                    {pack.cash > 0 ? `US$ ${pack.cash.toFixed(2)}` : ""}
                    {pack.cash > 0 && pack.gem > 0 ? " · " : ""}
                    {pack.gem > 0 && (
                      <>
                        {pack.gem}
                        <IoDiamond size={12} style={styles.gemIcon} />
                      </>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
        <div style={mq(styles.inputRow, styles.inputRowMobile)}>
          <button
            style={mq(styles.resetBtn, styles.resetBtnMobile)}
            onClick={() => {
              setInput("");
              resetChat();
            }}
            disabled={loading}
          >
            {t("chat.reset")}
          </button>

          {/* Doubles as the questions-left readout, so it shows even if the packs
              never loaded — it just can't open the panel then. */}
          {(chatProducts.length > 0 || chatCredit != null) && (
            <button
              style={mq(styles.topUpBtn, styles.resetBtnMobile)}
              onClick={() => chatProducts.length > 0 && setShowTopUp((v) => !v)}
              aria-expanded={showTopUp}
              title={chatCredit != null ? t("chat.topUp.questionsLeft", { n: chatCredit }) : undefined}
            >
              {showTopUp ? (
                t("chat.topUp.hidePacks")
              ) : chatCredit != null ? (
                <>
                  <IoChatbubbleEllipsesOutline size={13} style={styles.labelIcon} />
                  {t("chat.topUp.creditLeft", { n: chatCredit })}
                </>
              ) : (
                t("chat.topUp.addQuestions")
              )}
            </button>
          )}

          <div style={mq(styles.inputWrap, styles.inputWrapMobile)}>
            {/* <GrAttachment size={16} style={styles.attachIcon} /> */}
            <input
              style={mq(styles.input, styles.inputMobile)}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={outOfCredit}
              placeholder={t(outOfCredit ? "chat.outOfCreditPlaceholder" : "chat.inputPlaceholder")}
            />
            <button
              style={
                loading || outOfCredit || !input.trim()
                  ? { ...styles.sendBtn, ...styles.sendBtnDisabled }
                  : styles.sendBtn
              }
              onClick={() => handleSend(input)}
              disabled={loading || outOfCredit || !input.trim()}
              title={outOfCredit ? t("chat.outOfCreditTooltip") : undefined}
            >
              <IoIosSend size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Pack confirm: quantity, then gems (PayMode 0) or card (PayMode 1) ── */}
      {pendingPack && !stripeOpen && (
        <div style={styles.modalOverlay} onClick={() => !purchasing && setPendingPack(null)}>
          <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <p style={styles.modalTitle}>{pendingPack.label}</p>

            <div style={styles.qtyRow}>
              <button
                style={styles.qtyBtn}
                onClick={() => setPackQty((q) => Math.max(1, q - 1))}
                disabled={purchasing || packQty <= 1}
              >
                −
              </button>
              <span style={styles.qtyValue}>{packQty}</span>
              <button
                style={styles.qtyBtn}
                onClick={() => setPackQty((q) => q + 1)}
                disabled={purchasing}
              >
                +
              </button>
            </div>

            {pendingPack.questions != null && (
              <p style={styles.modalSubtitle}>
                {t("chat.purchase.questionsInTotal", { n: pendingPack.questions * packQty })}
              </p>
            )}

            <div style={styles.payRow}>
              <button
                style={styles.payBtn}
                onClick={handleGemPurchase}
                disabled={purchasing || packTotalGems <= 0}
              >
                {purchasing ? (
                  t("chat.purchase.processing")
                ) : (
                  <>
                    {t("chat.purchase.pay", { n: packTotalGems })}
                    <IoDiamond size={12} style={styles.gemIcon} />
                  </>
                )}
              </button>
              <button
                style={styles.payBtnAlt}
                onClick={handleCardPurchase}
                disabled={purchasing || packTotalCash <= 0}
              >
                {t("chat.purchase.card", { amount: packTotalCash.toFixed(2) })}
              </button>
            </div>

            <button
              style={styles.modalCancel}
              onClick={() => setPendingPack(null)}
              disabled={purchasing}
            >
              {t("chat.purchase.cancel")}
            </button>
          </div>
        </div>
      )}

      {/* ── Purchase outcome ── */}
      {purchaseResult && (
        <div style={styles.modalOverlay} onClick={() => setPurchaseResult(null)}>
          <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <p style={styles.modalTitle}>
              {purchaseResult.ok ? (
                <>
                  <IoCheckmarkCircle size={16} style={styles.labelIcon} />
                  {t("chat.purchase.successTitle")}
                </>
              ) : purchaseResult.insufficientGems ? (
                <>
                  <IoDiamond size={14} style={styles.gemIcon} />
                  {t("chat.purchase.notEnoughGems")}
                </>
              ) : (
                <>
                  <IoWarning size={16} style={styles.labelIcon} />
                  {t("chat.purchase.failedTitle")}
                </>
              )}
            </p>
            <p style={styles.modalSubtitle}>{purchaseResult.message}</p>
            {purchaseResult.insufficientGems && (
              <button
                style={styles.payBtn}
                onClick={() => {
                  setPurchaseResult(null);
                  navigate("/account", { state: { setactiveTab: "purchasecoin" } });
                }}
              >
                {t("chat.purchase.getMoreGems")}
              </button>
            )}
            <button
              style={purchaseResult.insufficientGems ? styles.payBtnAlt : styles.payBtn}
              onClick={() => setPurchaseResult(null)}
            >
              {t("chat.purchase.close")}
            </button>
          </div>
        </div>
      )}

      <StripePayment
        id={pendingPack?.id}
        amount={packTotalCash}
        currency="usd"
        isOpen={stripeOpen}
        onClose={() => setStripeOpen(false)}
        onPaymentSuccess={handleCardPaymentSuccess}
      />
    </div>
  );
};

/* ─────────────────────────────────────────────────────────── */
/*  Styles                                                     */
/* ─────────────────────────────────────────────────────────── */
const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 70px)", // offset for Tryoutpage topbar
    minHeight: 0,
    color: "#f5f5f5",
    fontFamily: "Arial, sans-serif",
  },
  // dvh tracks the collapsing mobile URL bar; 100vh pushes the composer off-screen
  pageMobile: {
    height: "calc(100dvh - 70px)",
    overflowX: "hidden",
  },
  backRow: {
    width: "1200px",
    maxWidth: "100%",
    margin: "0 auto",
    padding: "14px 0 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  backRowMobile: {
    padding: "10px 12px 0",
  },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    background: "transparent",
    border: "none",
    color: "#dead7a",
    fontSize: "0.9rem",
    cursor: "pointer",
    padding: 0,
  },
  resetBtn: {
    background: "transparent",
    border: "1px solid rgba(223,189,105,0.5)",
    color: "#DFBD69",
    borderRadius: "999px",
    padding: "6px 14px",
    fontSize: "0.82rem",
    cursor: "pointer",
    fontFamily: "'DMSans', serif",
  },
  resetBtnMobile: {
    padding: "8px 12px",
    fontSize: "0.75rem",
    flexShrink: 0,
  },

  /* ── Body ── */
  body: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "center",
    gap: "20px",
    width: "1200px",
    maxWidth: "100%",
    padding: "20px 24px",
    overflow: "hidden",
    margin: "0 auto",
    background: "#0a031e",
    borderRadius: "20px",
    border: "2px solid rgba(201,168,75,0.2)",
  },
  // Stack the two columns and hand scrolling to this one container
  bodyMobile: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: "14px",
    padding: "14px 12px",
    // width:auto lets the page's stretch apply, so the side margins don't
    // add on top of a 100% width and overflow the viewport
    width: "auto",
    margin: "0 10px",
    borderRadius: "14px",
    overflowY: "auto",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
  },
  chatColumn: {
    flex: 1,
    minWidth: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    overflowY: "auto",
  },
  chatColumnMobile: {
    flex: "0 0 auto",
    height: "auto",
    overflowY: "visible",
  },
  sidebar: {
    width: "260px",
    flexShrink: 0,
    height: "100%",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    paddingLeft: "16px",
    borderLeft: "1px solid rgba(201,168,75,0.15)",
  },
  // Below the thread, full width, cards scroll sideways instead of wrapping
  sidebarMobile: {
    width: "100%",
    height: "auto",
    overflowY: "visible",
    gap: "16px",
    paddingLeft: 0,
    paddingTop: "14px",
    borderLeft: "none",
    borderTop: "1px solid rgba(201,168,75,0.15)",
  },
  /* ── Welcome ── */
  welcome: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    maxWidth: "700px",
  },
  welcomeBox: {
    // background: "rgba(255,255,255,0.04)",
    // border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "16px",
    padding: "10px 40px 4px",
    width: "100%",
    textAlign: "center",
  },
  welcomeBoxMobile: {
    padding: "6px 4px 4px",
  },
  promptHeading: {
    color: "#DFBD69",
    fontSize: "1.05rem",
    fontWeight: "500",
    marginBottom: "0",
    lineHeight: 1.6,
  },

  /* ── Category cards ── */
  categoryRow: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",
    justifyContent: "center",
    width: "100%",
  },
  catCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "8px",
    background: "#0a031e",
    border: "1px solid rgba(201,168,75,0.4)",
    borderRadius: "14px",
    padding: "10px 14px",
    width: "120px",
    cursor: "pointer",
    textAlign: "left",
    transition: "border-color 0.2s, background 0.2s",
  },
  catIcon: {
    width: "25px",
    height: "25px",
    objectFit: "contain",
  },
  catLabel: {
    fontSize: "14px",
    lineHeight: "14px",
    fontWeight: "500",
    width: "100%",
    textAlign: "center",
    fontFamily: "'PlayfairDisplay', serif",
  },
  catDesc: {
    fontSize: "12px",
    fontWeight: "400",
    color: "#ffffff",
    lineHeight: "14px",
    fontFamily: "'DMSans', serif",
    opacity: 0.5,
  },

  /* ── Thread ── */
  thread: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    width: "100%",
    maxWidth: "700px",
    alignItems: "stretch",
  },
  bubble: {
    maxWidth: "75%",
    padding: "10px 14px",
    borderRadius: "12px",
    fontSize: "0.93rem",
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
    overflowWrap: "anywhere",
  },
  bubbleMobile: {
    maxWidth: "88%",
  },

  /* ── Suggestion chips ── */
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    alignSelf: "flex-start",
    marginTop: "4px",
  },
  chip: {
    background: "#1f1f1f",
    color: "#DFBD69",
    border: "1px solid rgba(223,189,105,0.4)",
    borderRadius: "999px",
    padding: "6px 14px",
    fontSize: "0.82rem",
    cursor: "pointer",
  },

  /* Inline icons that stand in for the old emoji — sized to sit on the text
     baseline of whatever label they lead. */
  labelIcon: {
    verticalAlign: "-2px",
    marginRight: "6px",
    flexShrink: 0,
  },
  gemIcon: {
    verticalAlign: "-1px",
    margin: "0 3px",
    flexShrink: 0,
  },
  /* Panel header — the single close affordance for all suggestions */
  panelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    paddingBottom: "8px",
    borderBottom: "1px solid rgba(201,168,75,0.15)",
  },
  panelTitle: {
    display: "inline-flex",
    alignItems: "center",
    color: "#DFBD69",
    fontSize: "0.85rem",
    fontFamily: "'DMSans', sans-serif",
    fontWeight: "700",
    margin: 0,
    letterSpacing: "0.4px",
  },
  panelCloseBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    color: "#DFBD69",
    opacity: 0.65,
    cursor: "pointer",
    padding: "2px",
    marginTop: "-2px",
    flexShrink: 0,
    lineHeight: 0,
  },
  // Bigger tap target on touch screens
  panelCloseBtnMobile: {
    padding: "6px",
    margin: "-6px -4px -6px 0",
  },
  /* ── Shop suggestion strip ── */
  shopStrip: {
    alignSelf: "flex-start",
    width: "100%",
    marginTop: "8px",
  },
  shopStripLabel: {
    color: "#DFBD69",
    fontSize: "0.8rem",
    fontFamily: "'DMSans', sans-serif",
    fontWeight: "700",
    margin: "0 0 8px",
    letterSpacing: "0.4px",
  },
  shopCards: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  shopCardsMobile: {
    flexWrap: "nowrap",
    overflowX: "auto",
    paddingBottom: "6px",
    WebkitOverflowScrolling: "touch",
  },
  // Keeps cards from being squeezed inside the horizontal strip
  cardMobile: {
    flexShrink: 0,
  },
  shopCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    background: "#0a031e",
    border: "1px solid rgba(201,168,75,0.35)",
    borderRadius: "12px",
    padding: "10px",
    width: "110px",
    cursor: "pointer",
    textAlign: "center",
    transition: "border-color 0.2s",
  },
  shopCardImg: {
    width: "80px",
    height: "80px",
    objectFit: "contain",
    borderRadius: "8px",
    background: "#12042e",
  },
  shopCardImgPlaceholder: {
    width: "80px",
    height: "80px",
    borderRadius: "8px",
    background: "#12042e",
  },
  shopCardName: {
    color: "#fff2d2",
    fontSize: "0.72rem",
    fontFamily: "'DMSans', sans-serif",
    lineHeight: 1.3,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "100%",
  },
  shopCardPrice: {
    color: "#DFBD69",
    fontSize: "0.75rem",
    fontFamily: "'DMSans', sans-serif",
    fontWeight: "700",
  },

  /* ── Consultation suggestion strip ── */
  consultationStrip: {
    alignSelf: "flex-start",
    width: "100%",
    marginTop: "8px",
  },
  consultationCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    background: "#0a031e",
    border: "1px solid rgba(223,189,105,0.5)",
    borderRadius: "12px",
    padding: "10px",
    width: "130px",
    cursor: "pointer",
    textAlign: "center",
    transition: "border-color 0.2s",
  },
  consultationCardImg: {
    width: "70px",
    height: "70px",
    objectFit: "cover",
    borderRadius: "50%",
    background: "#12042e",
  },
  consultationCardImgPlaceholder: {
    width: "70px",
    height: "70px",
    borderRadius: "50%",
    background: "#12042e",
  },
  consultationCardName: {
    color: "#fff2d2",
    fontSize: "0.78rem",
    fontFamily: "'DMSans', sans-serif",
    fontWeight: "700",
    lineHeight: 1.3,
  },
  consultationCardReason: {
    color: "#cfcfcf",
    fontSize: "0.68rem",
    fontFamily: "'DMSans', sans-serif",
    lineHeight: 1.3,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  consultationCardCta: {
    color: "#DFBD69",
    fontSize: "0.72rem",
    fontFamily: "'DMSans', sans-serif",
    fontWeight: "700",
    marginTop: "2px",
  },

  /* ── Question top-up packs ── */
  topUpBtn: {
    background: "rgba(223,189,105,0.12)",
    border: "1px solid rgba(223,189,105,0.5)",
    color: "#DFBD69",
    borderRadius: "999px",
    padding: "6px 14px",
    fontSize: "0.82rem",
    cursor: "pointer",
    fontFamily: "'DMSans', serif",
    whiteSpace: "nowrap",
  },
  topUpPanel: {
    width: "100%",
    maxWidth: "760px",
    margin: "0 auto 12px",
  },
  topUpPanelMobile: {
    margin: "0 auto 8px",
  },
  topUpLabel: {
    color: "#DFBD69",
    fontSize: "0.8rem",
    fontFamily: "'DMSans', sans-serif",
    fontWeight: "700",
    margin: "0 0 8px",
    letterSpacing: "0.4px",
  },
  topUpCards: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  topUpCardsMobile: {
    flexWrap: "nowrap",
    overflowX: "auto",
    paddingBottom: "6px",
    WebkitOverflowScrolling: "touch",
  },
  topUpCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
    background: "#0a031e",
    border: "1px solid rgba(201,168,75,0.35)",
    borderRadius: "12px",
    padding: "10px 14px",
    minWidth: "120px",
    cursor: "pointer",
    textAlign: "center",
    transition: "border-color 0.2s",
  },
  topUpCardCount: {
    color: "#fff2d2",
    fontSize: "1.25rem",
    fontFamily: "'DMSans', sans-serif",
    fontWeight: "700",
    lineHeight: 1.1,
  },
  topUpCardName: {
    color: "rgba(255,242,210,0.7)",
    fontSize: "0.7rem",
    fontFamily: "'DMSans', sans-serif",
  },
  topUpCardPrice: {
    color: "#DFBD69",
    fontSize: "0.75rem",
    fontFamily: "'DMSans', sans-serif",
    fontWeight: "700",
  },

  /* ── Pack purchase modals ── */
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2500,
    padding: "20px",
  },
  modalBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "14px",
    background: "#0a031e",
    border: "1px solid rgba(201,168,75,0.45)",
    borderRadius: "16px",
    padding: "24px",
    width: "320px",
    maxWidth: "100%",
    textAlign: "center",
  },
  modalTitle: {
    color: "#fff2d2",
    fontSize: "1.05rem",
    fontFamily: "'DMSans', sans-serif",
    fontWeight: "700",
    margin: 0,
  },
  modalSubtitle: {
    color: "rgba(255,242,210,0.75)",
    fontSize: "0.82rem",
    fontFamily: "'DMSans', sans-serif",
    margin: 0,
    lineHeight: 1.4,
  },
  qtyRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  qtyBtn: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    background: "transparent",
    border: "1px solid rgba(223,189,105,0.5)",
    color: "#DFBD69",
    fontSize: "1.1rem",
    lineHeight: 1,
    cursor: "pointer",
  },
  qtyValue: {
    color: "#fff2d2",
    fontSize: "1.2rem",
    fontFamily: "'DMSans', sans-serif",
    fontWeight: "700",
    minWidth: "28px",
  },
  payRow: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
  },
  payBtn: {
    background: "#DFBD69",
    border: "none",
    borderRadius: "999px",
    color: "#1a1a1a",
    padding: "10px 16px",
    fontSize: "0.85rem",
    fontFamily: "'DMSans', sans-serif",
    fontWeight: "700",
    cursor: "pointer",
    width: "100%",
  },
  payBtnAlt: {
    background: "transparent",
    border: "1px solid rgba(223,189,105,0.5)",
    borderRadius: "999px",
    color: "#DFBD69",
    padding: "10px 16px",
    fontSize: "0.85rem",
    fontFamily: "'DMSans', sans-serif",
    fontWeight: "700",
    cursor: "pointer",
    width: "100%",
  },
  modalCancel: {
    background: "transparent",
    border: "none",
    color: "rgba(255,242,210,0.6)",
    fontSize: "0.78rem",
    fontFamily: "'DMSans', sans-serif",
    cursor: "pointer",
  },

  /* ── Input bar ── */
  inputBar: {
    padding: "16px 24px 24px",
  },
  inputBarMobile: {
    padding: "10px 12px 14px",
    flexShrink: 0,
  },
  /* ── "Suggestions below" pill ── */
  suggestHintRow: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "8px",
  },
  suggestHintBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "rgba(223,189,105,0.12)",
    border: "1px solid rgba(223,189,105,0.5)",
    color: "#DFBD69",
    borderRadius: "999px",
    padding: "7px 14px",
    fontSize: "0.78rem",
    fontFamily: "'DMSans', sans-serif",
    fontWeight: "700",
    cursor: "pointer",
  },
  suggestHintDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    background: "#DFBD69",
    flexShrink: 0,
  },

  inputRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    maxWidth: "760px",
    margin: "0 auto",
  },
  inputRowMobile: {
    gap: "8px",
  },
  inputWrap: {
    display: "flex",
    alignItems: "center",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: "999px",
    padding: "10px 16px",
    flex: 1,
    gap: "10px",
  },
  inputWrapMobile: {
    padding: "6px 6px 6px 14px",
    minWidth: 0,
    gap: "6px",
  },
  attachIcon: {
    color: "#aaa",
    flexShrink: 0,
    cursor: "pointer",
  },
  input: {
    flex: 1,
    minWidth: 0, // lets the field shrink instead of pushing the send button out
    background: "transparent",
    border: "none",
    outline: "none",
    color: "#f5f5f5",
    fontSize: "0.93rem",
    placeholder: "#888",
  },
  // 16px keeps iOS from zooming the page on focus
  inputMobile: {
    fontSize: "16px",
  },
  catCardDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
  },
  chipDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
  },
  sendBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#DFBD69",
    border: "none",
    borderRadius: "50%",
    width: "34px",
    height: "34px",
    cursor: "pointer",
    color: "#1a1a1a",
    flexShrink: 0,
    opacity: 1,
  },
  sendBtnDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
  },
};

export default Chat;
